import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import SignaturePad from '../components/SignaturePad';
import api from '../api/axios';

const CACHE_BUSTER = Date.now();

const Register = () => {
    const { register, login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [userData, setUserData] = useState({
        name: '', last_name: '', email: '', identification: '', city: '', country: 'Colombia', representative: '', signature: null
    });
    const [signatureAccepted, setSignatureAccepted] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [eventStarted, setEventStarted] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkEventStatus = async () => {
            try {
                const res = await api.get('/agenda?status=active');
                if (res.data && res.data.length > 0) {
                    const sorted = [...res.data].sort((a, b) => {
                        const dateA = new Date(`${a.date.split('T')[0]}T${a.start_time}`);
                        const dateB = new Date(`${b.date.split('T')[0]}T${b.start_time}`);
                        return dateA - dateB;
                    });
                    const earliest = new Date(`${sorted[0].date.split('T')[0]}T${sorted[0].start_time}`);
                    const now = new Date();
                    if (now >= earliest) {
                        setEventStarted(true);
                    }
                }
            } catch (err) {
                console.error("Error checking event status", err);
            }
        };
        checkEventStatus();
    }, []);

    const isCoherentWord = (str) => {
        if (!str || str.trim().length === 0) return false;
        const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;
        if (!regex.test(str)) return false;
        if (/(.)\1\1/i.test(str)) return false;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (reg.identification !== false) {
            if (!/^[0-9]+$/.test(userData.identification)) {
                setError('La identificación debe contener solo números.');
                setIsLoading(false);
                return;
            }
        }

        if (reg.terms_accepted && !termsAccepted) {
            setError('Debe aceptar el tratamiento de datos para continuar.');
            setIsLoading(false);
            return;
        }

        if (reg.name !== false && !isCoherentWord(userData.name)) {
            setError('El nombre ingresado no es válido.');
            setIsLoading(false);
            return;
        }

        if (reg.last_name !== false && !isCoherentWord(userData.last_name)) {
            setError('El apellido ingresado no es válido.');
            setIsLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userData.email)) {
            setError('Debe ingresar un correo electrónico válido.');
            setIsLoading(false);
            return;
        }

        // Map identification to email if disabled
        const finalData = { ...userData };
        if (config?.registration_fields?.identification === false) {
            finalData.identification = userData.email;
        }

        const result = await register(finalData);
        if (result.success) {
            await login(finalData.identification);
            const fromState = location.state?.from;
            let targetPath = '/';
            if (fromState) {
                targetPath = `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`;
            }
            navigate(targetPath, { replace: true });
        } else {
            setError(result.message);
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
            </div>
        );
    }

    const reg = config?.registration_fields || {};
    const pageDesign = config?.pageDesigns?.register || {};

    const getColSpan = (field, partner) => {
        if (reg[partner] === false) return "col-span-2";
        return "col-span-1";
    };

    const sectionBg = pageDesign?.bgImage ? `${pageDesign.bgImage}?v=${CACHE_BUSTER}` : '';
    const globalBg = isMobile
        ? (config?.backgroundMobile || config?.backgroundImage ? `${config.backgroundMobile || config.backgroundImage}?v=${CACHE_BUSTER}` : '')
        : (config?.backgroundDesktop || config?.backgroundImage ? `${config.backgroundDesktop || config.backgroundImage}?v=${CACHE_BUSTER}` : '');
    const bottomBg = sectionBg || globalBg;

    const logoUrl = config?.logoUrl ? `${config.logoUrl}?v=${CACHE_BUSTER}` : '';

    const primaryColor = config?.primaryColor || "#0a58ca";
    const secondaryColor = config?.secondaryColor || "#ffa800";

    const containerStyle = {
        backgroundColor: pageDesign.backgroundColor || config?.backgroundColor || '#ffffff',
        color: pageDesign.globalFontColor || config?.globalFontColor || '#000000'
    };

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col relative overflow-x-hidden font-sans items-center"
        >
            {/* Background Layer */}
            {bottomBg && (
                <div className="absolute inset-0 pointer-events-none z-0">
                    <img
                        src={bottomBg}
                        alt="Fondo Sección"
                        className="w-full h-full object-cover object-bottom"
                    />
                </div>
            )}

            {/* Configured Global Logo */}
            <div className="relative z-50 w-full flex justify-center pointer-events-none flex-shrink-0">
                <img
                    src={logoUrl}
                    alt="Logo Global"
                    className="w-full h-auto object-contain drop-shadow-md"
                />
            </div>

            {/* Removed dynamic visual elements to prevent obsolete text rendering */}

            {/* Main Form Container matched to Mockup */}
            <div className="w-full max-w-md z-10 space-y-0 bg-white border-2 rounded-3xl shadow-[0_15px_35px_rgba(0,0,0,0.15)] relative mt-10 mb-20 animate-fadeIn" style={{ borderColor: '#033f83' }}>

                {/* Floating Title Box */}
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-20 w-[90%] sm:w-[85%] flex justify-center">
                    <div className="bg-[#033f83] text-white py-3 px-6 text-center font-black text-lg sm:text-xl rounded-md shadow-md uppercase tracking-wider w-full">
                        REGISTRO
                    </div>
                </div>

                <div className="pt-12 pb-8 px-6 sm:px-8 relative">
                    <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-y-6 gap-x-4">
                        {/* Identification */}
                        {reg.identification !== false && (
                            <div className="col-span-2 space-y-1">
                                <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                    Cédula*
                                </label>
                                <input
                                    className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={userData.identification}
                                    onChange={(e) => setUserData({ ...userData, identification: e.target.value.replace(/\D/g, '') })}
                                    required
                                />
                            </div>
                        )}

                        {/* Name */}
                        {reg.name !== false && (
                            <div className={`${getColSpan('name', 'last_name')} space-y-1`}>
                                <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                    Nombres*
                                </label>
                                <input
                                    className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={userData.name}
                                    onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Last Name */}
                        {reg.last_name !== false && (
                            <div className={`${getColSpan('last_name', 'name')} space-y-1`}>
                                <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                    Apellidos*
                                </label>
                                <input
                                    className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={userData.last_name}
                                    onChange={(e) => setUserData({ ...userData, last_name: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Email */}
                        <div className="col-span-2 space-y-1">
                            <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                Correo Electrónico*
                            </label>
                            <input
                                type="email"
                                className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                value={userData.email} // Mapped to email
                                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                                required
                            />
                        </div>

                        {/* City */}
                        {reg.city !== false && (
                            <div className={`${getColSpan('city', 'country')} space-y-1`}>
                                <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                    Ciudad*
                                </label>
                                <input
                                    className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={userData.city}
                                    onChange={(e) => setUserData({ ...userData, city: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Country */}
                        {reg.country !== false && (
                            <div className={`${getColSpan('country', 'city')} space-y-1`}>
                                <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                    País*
                                </label>
                                <input
                                    className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={userData.country}
                                    onChange={(e) => setUserData({ ...userData, country: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Representative */}
                        {reg.representative !== false && (
                            <div className="col-span-2 space-y-1">
                                <label className="text-[#033f83] text-[12px] font-bold block text-left mb-1.5 ml-1">
                                    Institución de trabajo
                                </label>
                                <input
                                    className="w-full bg-white border-[1.5px] border-[#033f83] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm"
                                    value={userData.representative}
                                    onChange={(e) => setUserData({ ...userData, representative: e.target.value })}
                                />
                            </div>
                        )}

                        {/* Signature */}
                        {!(reg.signature_by_date && !eventStarted) && (
                            <div className="col-span-2 space-y-3 pt-3 border-t border-slate-100 mt-2">
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <div className="relative flex items-center justify-center mt-1">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={signatureAccepted}
                                            onChange={(e) => {
                                                setSignatureAccepted(e.target.checked);
                                                if (!e.target.checked) setUserData({ ...userData, signature: null });
                                            }}
                                        />
                                        <div
                                            style={{ borderColor: `${primaryColor}66` }}
                                            className="w-5 h-5 rounded-md border-2 bg-white peer-checked:bg-[#0a58ca] peer-checked:border-[#0a58ca] transition-all flex items-center justify-center shadow-inner"
                                        >
                                            <svg className={`w-3 h-3 text-white transform transition-transform ${signatureAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium leading-snug select-none text-left">
                                        Activar firma digital.
                                    </span>
                                </label>

                                {signatureAccepted && (
                                    <div className="animate-fadeIn w-full bg-slate-50 p-3 rounded-lg border border-slate-200">
                                        <label className="block text-[9px] uppercase font-black tracking-widest text-slate-400 mb-1 text-center">Firme aquí</label>
                                        <SignaturePad onSignatureChange={(sig) => setUserData({ ...userData, signature: sig })} />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Terms & Conditions matching Image 4 */}
                        <div className="col-span-2 pt-2 px-2 flex flex-col gap-3">
                            <span className="text-[#033f83] text-[11px] font-bold block">* Campos obligatorios</span>

                            {reg.terms_accepted && (
                                <label className="flex items-start gap-3 cursor-pointer group mt-2">
                                    <div className="relative flex items-center justify-center mt-0.5">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={termsAccepted}
                                            onChange={(e) => setTermsAccepted(e.target.checked)}
                                        />
                                        <div className="w-5 h-5 border-[1.5px] border-[#033f83] bg-white peer-checked:bg-[#033f83] transition-all flex items-center justify-center shadow-sm">
                                            <svg className={`w-3.5 h-3.5 text-white transform transition-transform ${termsAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <span className="text-[11px] text-[#033f83] font-bold leading-tight select-none">
                                        Acepto el tratamiento de datos personales,<br />
                                        <a
                                            href={reg.terms_url || '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Más informacion aquí
                                        </a>
                                    </span>
                                </label>
                            )}
                        </div>

                        {/* Submit Section */}
                        <div className="col-span-2 pt-6 flex justify-center">
                            {error && (
                                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-[10px] font-black uppercase tracking-wider border border-red-200 text-center mb-4 w-full">
                                    {error}
                                </div>
                            )}

                            <div className="w-[80%] relative">
                                <button
                                    type="submit"
                                    disabled={isLoading || (signatureAccepted && !userData.signature) || (reg.terms_accepted && !termsAccepted)}
                                    className="w-full bg-[#033f83] text-white py-3.5 rounded-sm font-black text-xl shadow-[0_5px_15px_rgba(3,63,131,0.4)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                                >
                                    {isLoading ? 'ENVIANDO...' : 'Enviar'}
                                </button>
                            </div>
                        </div>

                        <div className="col-span-2 text-center pt-2">
                            <Link to="/login" className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-[#033f83] transition-colors">
                                ← Volver al Ingreso
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            {/* Gallery perspective image placed inline instead of fixed background */}
            <div className="w-full pointer-events-none mt-2 relative -mx-4 sm:-mx-6 max-w-[100vw] overflow-hidden flex justify-center">
                <img
                    src={bottomBg}
                    alt="Gallery Background"
                    className="w-full object-cover object-top"
                />
            </div>

            {pageDesign?.showLegalText && pageDesign?.legalTextUrl && (
                <footer className="w-full p-6 flex justify-center items-center z-40 relative mt-auto select-none pointer-events-none">
                    <img
                        src={`${pageDesign.legalTextUrl}${pageDesign.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                        alt="Información Legal"
                        className="max-w-[90vw] md:max-w-2xl h-auto object-contain"
                    />
                </footer>
            )}
        </div>
    );
};

export default Register;
